import { memo, useCallback, useMemo } from 'react';
import { BaseAnalyticsFilter } from '@repo/shared/dtos/analytics/AnalyticsFilterDto';
import { useDateRange } from '../hooks/useDateRange';
import { hasActiveFilters } from '../filterHelpers';
import FilterBadge from './FilterBadge';

interface SelectedFiltersDisplayProps {
  filters: BaseAnalyticsFilter;
  onFiltersChange: (filters: BaseAnalyticsFilter) => void;
  getUnitName?: (id: number) => string;
  getExperienceName?: (id: number) => string;
}

const SelectedFiltersDisplay = memo(function SelectedFiltersDisplay({
  filters,
  onFiltersChange,
  getUnitName = (id: number) => `Unit ${id}`,
  getExperienceName = (id: number) => `Session ${id}`,
}: SelectedFiltersDisplayProps) {
  const { formatRange } = useDateRange();

  const hasFiltersActive = useMemo(() => hasActiveFilters(filters), [filters]);

  const removeUnit = useCallback(
    (unitId: number) => {
      onFiltersChange({
        ...filters,
        unitIds: filters.unitIds?.filter(id => id !== unitId) || [],
      });
    },
    [filters, onFiltersChange],
  );

  const removeExperience = useCallback(
    (experienceId: number) => {
      onFiltersChange({
        ...filters,
        experienceIds: filters.experienceIds?.filter(id => id !== experienceId) || [],
      });
    },
    [filters, onFiltersChange],
  );

  const removeDateRange = useCallback(() => {
    onFiltersChange({
      ...filters,
      startDate: undefined,
      endDate: undefined,
      granularity: 'month',
    });
  }, [filters, onFiltersChange]);

  const removeSessionTitle = useCallback(() => {
    onFiltersChange({
      ...filters,
      experienceTitle: undefined,
    });
  }, [filters, onFiltersChange]);

  if (!hasFiltersActive) {
    return null;
  }

  return (
    <div className="flex flex-wrap items-center gap-1.5 border-b border-[var(--anl-border)] bg-[var(--anl-surface-elev)] px-3 py-1.5 md:px-6">
      <span className="text-xs font-medium text-slate-400">Active filters:</span>

      {(filters.startDate || filters.endDate) && (
        <FilterBadge
          label={formatRange(filters.startDate, filters.endDate)}
          onRemove={removeDateRange}
          variant="date"
        />
      )}

      {filters.experienceTitle && (
        <FilterBadge
          label={`Session: ${filters.experienceTitle}`}
          onRemove={removeSessionTitle}
          variant="experienceTitle"
        />
      )}

      {filters.unitIds?.map(unitId => (
        <FilterBadge
          key={`unit-${unitId}`}
          label={getUnitName(unitId)}
          onRemove={() => removeUnit(unitId)}
          variant="unit"
        />
      ))}

      {filters.experienceIds?.map(experienceId => (
        <FilterBadge
          key={`experience-${experienceId}`}
          label={getExperienceName(experienceId)}
          onRemove={() => removeExperience(experienceId)}
          variant="experience"
        />
      ))}
    </div>
  );
});

export default SelectedFiltersDisplay;
